<template>
  <app-modal :model-value="isOpen"
             size="lg"
             :ok-text="$t('project.documents.dialogs.DocumentsExistingTreeDialog.import')"
             :ok-disabled="!selectedFile"
             :ok-loading="postMassFoldersPending"
             :cancel-disabled="postMassFoldersPending"
             data-locator="documents-folder-structure-dialog"
             @ok="onOk"
             @cancel="onCancel"
  >
    <template #title>{{ $t('project.documents.dialogs.DocumentsExistingTreeDialog.title') }}</template>
    <template #body>
      <v-row>
        <v-col>
        <i18n-t keypath="project.documents.dialogs.DocumentsExistingTreeDialog.textInfo"
                tag="span"
                scope="global">
          <template v-slot:first>
            <b>(1)</b>
          </template>
          <template v-slot:second>
            <b>(2)</b>
          </template>
        </i18n-t>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <AppDownloadFile :link="templateLink"
                           class="w-100 h-100"
                           data-locator="download-template-button"
          >
            <template #default="{submitForm}">
              <div class="ChoiceCard"
                   @click="submitForm"
              >
                <div class="d-inline-flex align-center ml-n3">
                  <div class="mr-3 step-chip">1</div>
                  <h3 class="font-weight-medium">{{ $t('project.documents.dialogs.DocumentsExistingTreeDialog.downloadTemplate') }}</h3>
                </div>
                <div class="mt-5">
                  <v-img src="@/assets/img/icons/excel_import.svg"
                         class="mx-auto"
                         width="45"
                  />
                </div>
              </div>
            </template>
          </AppDownloadFile>
        </v-col>
        <v-col>
          <div class="ChoiceCard"
               data-locator="upload-template-button"
               @click="onFileInputClick"
          >
            <div class="d-inline-flex align-center ml-n3">
              <div class="mr-3 step-chip">2</div>
              <h3 class="font-weight-medium">{{ $t('project.documents.dialogs.DocumentsExistingTreeDialog.uploadFile') }}</h3>
            </div>
            <div class="mt-5">
              <v-img src="@/assets/img/icons/excel_closd.svg"
                     class="mx-auto"
                     width="45"
              />
            </div>
            <div v-if="!selectedFile" class="mt-5 text-caption">{{ $t('project.documents.dialogs.DocumentsExistingTreeDialog.uploadedFileInfo') }}</div>
            <div v-else class="mt-5 ">{{selectedFile.name}}</div>
            <input id="fileInput"
                   ref="fileInput"
                   type="file"
                   class="d-none"
                   name="fileInput"
                   accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                   @input="selectFile"
            />
          </div>
        </v-col>
      </v-row>
      <v-row v-if="postFileResponse && postFileResponse.errors">
        <v-col>
          <div class="text-body-1 mb-3">{{ $t('project.documents.dialogs.DocumentsExistingTreeDialog.errorsInFile') }}</div>
          <ul style="list-style-position: inside;" class="text-left text-error">
            <li v-for="(error, i) in postFileResponse.errors" :key="`errorLine-${i}`" class="py-1">
              {{ $t('project.documents.dialogs.DocumentsExistingTreeDialog.error.atLine')}} {{Number(i) }}
              <ul style="list-style-position: inside;" class="ml-5">
                <li v-for="(errorDetail, j) in error" :key="`error-${j}`">
                  {{ $t("project.documents.dialogs.DocumentsExistingTreeDialog.error." + errorDetail) }}
                </li>
              </ul>
            </li>
          </ul>
        </v-col>
      </v-row>
    </template>
  </app-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { LOAD_FOLDERS_TREE, GET_PAGINATED_DOCUMENTS, GET_NUMBERING, POST_MASS_FOLDERS } from '@/store/modules/documents/action_types'

export default {
  name: 'DocumentsExistingTreeDialog',
  components: { AppDownloadFile },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      selectedFile: null,
      postFileResponse: null,
    }
  },
  computed: {
    ...mapState('documents', ['postMassFoldersPending', 'numberingEnabled']),
    templateLink () {
      return `${import.meta.env.VUE_APP_API_URL}/resources/massFoldersImportTemplate`
    },
  },
  methods: {
    ...mapActions('documents', [LOAD_FOLDERS_TREE, POST_MASS_FOLDERS, GET_PAGINATED_DOCUMENTS, GET_NUMBERING]),
    async onOk () {
      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile, this.selectedFile.name)
        await this.POST_MASS_FOLDERS({
          mnemo: this.mnemo,
          data: formData,
        })
        this.GET_PAGINATED_DOCUMENTS(0)
        if (!this.numberingEnabled) {
          this.GET_NUMBERING(this.mnemo)
        }
        this.LOAD_FOLDERS_TREE()
        this.onCancel()
      } catch (error) {
        console.error(error.response)
        this.postFileResponse = error.response.data
      }
    },
    onCancel () {
      this.selectedFile = null
      this.postFileResponse = null
      this.$refs.fileInput.value = null
      this.$emit('update:isOpen', false)
    },
    onFileInputClick () {
      this.$refs.fileInput.click()
    },
    selectFile (e) {
      this.selectedFile = e.target.files[0]
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.ChoiceCard {
  height: 100%;
  text-align: center;
  padding: 20px;
  border: get-border($width: 'border-width-2');
  // Rounded from 7px, no matching token, closest is border-radius-3 (8px) (AB#3108019).
  @include border-radius('border-radius-3');
  cursor: pointer;
  &:hover {
    border-color: #4C75B5;
  }
}

.step-chip {
  background: rgb(var(--v-theme-tertiary));
  color: white;
  @include border-radius('border-radius-circle');
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
